import { useState, useEffect, useRef } from 'react';
import { addMonths, subMonths, startOfMonth } from 'date-fns';
import { useSearchParams } from 'react-router-dom';

/**
 * 月のナビゲーションを管理するカスタムフック
 * 患者IDごとに月を保持し、利用者切り替え時に復元します
 *
 * @param initialMonth - 初期表示月（デフォルト: 当月）
 * @param syncWithUrlDate - URL パラメータ `date` から初期月を取得するか（デフォルト: false）
 * @param patientId - 患者ID（利用者切り替え検知用）
 * @returns 現在の月とナビゲーション関数
 */
export const useMonthNavigation = (
  initialMonth?: Date,
  syncWithUrlDate: boolean = false,
  patientId?: number | null
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // 患者IDごとの月を保存するMap
  const [patientMonths, setPatientMonths] = useState<Map<number, Date>>(new Map());

  // 前回の患者IDを追跡
  const prevPatientIdRef = useRef<number | null | undefined>(null);

  // 初期月の決定
  const getInitialMonth = (): Date => {
    if (syncWithUrlDate) {
      const dateParam = searchParams.get('date');
      if (dateParam) {
        const parsedDate = new Date(dateParam);
        if (!isNaN(parsedDate.getTime())) {
          // 日付パラメータがある場合は、その月の初日を返す
          return startOfMonth(parsedDate);
        }
      }
    }
    return initialMonth || new Date();
  };

  const [currentMonth, setCurrentMonth] = useState<Date>(getInitialMonth);

  // 患者IDが変わった場合の処理
  useEffect(() => {
    const prevPatientId = prevPatientIdRef.current;

    if (patientId !== null && patientId !== undefined) {
      // 前回の患者IDと異なる場合（利用者切り替え）
      if (prevPatientId !== null && prevPatientId !== undefined && prevPatientId !== patientId) {
        // 1. 前の患者の現在月を保存
        setPatientMonths(prev => {
          const newMap = new Map(prev);
          newMap.set(prevPatientId, currentMonth);
          return newMap;
        });

        // 2. 新しい患者の保存済み月を復元
        const savedMonth = patientMonths.get(patientId);
        if (savedMonth) {
          // 保存された月がある場合は復元
          setCurrentMonth(savedMonth);
        } else {
          // 初めての患者：現在の月をリセット
          setCurrentMonth(new Date());
        }
      }
    }

    // 前回の患者IDを更新
    prevPatientIdRef.current = patientId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  /**
   * 月を変更して患者IDごとのMapにも保存
   */
  const updateMonth = (newMonth: Date) => {
    setCurrentMonth(newMonth);

    // 現在の患者IDの月をMapに保存
    if (patientId !== null && patientId !== undefined) {
      setPatientMonths(prev => {
        const newMap = new Map(prev);
        newMap.set(patientId, newMonth);
        return newMap;
      });
    }
  };

  /**
   * 前の月に移動
   */
  const goToPrevMonth = () => {
    const prevMonth = subMonths(currentMonth, 1);
    updateMonth(prevMonth);
  };

  /**
   * 次の月に移動
   */
  const goToNextMonth = () => {
    const nextMonth = addMonths(currentMonth, 1);
    updateMonth(nextMonth);
  };

  /**
   * 今月に移動
   */
  const goToCurrentMonth = () => {
    updateMonth(new Date());
  };

  /**
   * 特定の月に移動
   */
  const goToMonth = (date: Date) => {
    updateMonth(date);
  };

  return {
    currentMonth,
    setCurrentMonth,
    goToPrevMonth,
    goToNextMonth,
    goToCurrentMonth,
    goToMonth,
  };
};
